import CircularProgress from "@mui/material/CircularProgress";
import { useCallback, useEffect, useRef, useState } from "react";

import appleLogo from "assets/images/small-logos/logo-apple.svg";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

const APPLE_IDENTITY_SCRIPT_ID = "matcha-apple-identity-script";
const APPLE_BUTTON_MIN_HEIGHT = "49px";
const SOCIAL_BUTTON_RADIUS = "0.9rem";
const APPLE_WEB_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID?.trim() || "";
const APPLE_WEB_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI?.trim() || "";

export const isAppleSignInConfigured = Boolean(APPLE_WEB_CLIENT_ID && APPLE_WEB_REDIRECT_URI);

interface AppleSignInResponse {
  authorization?: {
    code?: string;
    id_token?: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

interface AppleAuthApi {
  init: (options: {
    clientId: string;
    nonce?: string;
    redirectURI: string;
    scope?: string;
    state?: string;
    usePopup?: boolean;
  }) => void;
  signIn: () => Promise<AppleSignInResponse>;
}

interface AppleIdentityWindow extends Window {
  AppleID?: {
    auth?: AppleAuthApi;
  };
}

interface AppleCredential {
  firstName?: string;
  idToken: string;
  lastName?: string;
}

interface AppleSignInButtonProps {
  authFailedMessage: string;
  credentialMissingMessage: string;
  isSubmitting: boolean;
  label: string;
  loadFailedMessage: string;
  onCredential: (credential: AppleCredential) => Promise<void>;
  onError: (message: string) => void;
}

function getAppleIdentityWindow(): AppleIdentityWindow | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window as AppleIdentityWindow;
}

function loadAppleIdentityScript(): Promise<void> {
  const appleWindow = getAppleIdentityWindow();
  if (!appleWindow) {
    return Promise.reject(new Error("Sign in with Apple is unavailable."));
  }

  if (appleWindow.AppleID?.auth) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let scriptElement = document.getElementById(
      APPLE_IDENTITY_SCRIPT_ID
    ) as HTMLScriptElement | null;

    const handleLoad = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error("Unable to load Sign in with Apple."));
    };

    const cleanup = () => {
      scriptElement?.removeEventListener("load", handleLoad);
      scriptElement?.removeEventListener("error", handleError);
    };

    if (!scriptElement) {
      scriptElement = document.createElement("script");
      scriptElement.id = APPLE_IDENTITY_SCRIPT_ID;
      scriptElement.src =
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      scriptElement.async = true;
      scriptElement.defer = true;
      document.head.appendChild(scriptElement);
    }

    scriptElement.addEventListener("load", handleLoad);
    scriptElement.addEventListener("error", handleError);
  });
}

function createAppleRequestToken(): string {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isAppleSignInCanceled(error: unknown): boolean {
  if (typeof error === "string") {
    return /cancel|popup_closed/i.test(error);
  }

  if (!error || typeof error !== "object") {
    return false;
  }

  const message =
    ("error" in error && typeof error.error === "string" && error.error) ||
    ("message" in error && typeof error.message === "string" && error.message) ||
    "";

  return /cancel|popup_closed/i.test(message);
}

export function AppleSignInButton({
  authFailedMessage,
  credentialMissingMessage,
  isSubmitting,
  label,
  loadFailedMessage,
  onCredential,
  onError,
}: AppleSignInButtonProps) {
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  const [isReady, setIsReady] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!isAppleSignInConfigured) {
      return;
    }

    let isActive = true;

    loadAppleIdentityScript()
      .then(() => {
        if (!isActive) {
          return;
        }

        setIsReady(true);
        setHasLoadError(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setHasLoadError(true);
        onErrorRef.current(loadFailedMessage);
      });

    return () => {
      isActive = false;
    };
  }, [loadFailedMessage]);

  const handleClick = useCallback(async () => {
    const appleAuth = getAppleIdentityWindow()?.AppleID?.auth;

    if (!isReady || isSubmitting || !appleAuth) {
      return;
    }

    try {
      appleAuth.init({
        clientId: APPLE_WEB_CLIENT_ID,
        nonce: createAppleRequestToken(),
        redirectURI: APPLE_WEB_REDIRECT_URI,
        scope: "name email",
        state: createAppleRequestToken(),
        usePopup: true,
      });

      const response = await appleAuth.signIn();
      const idToken = response.authorization?.id_token?.trim() ?? "";

      if (!idToken) {
        onErrorRef.current(credentialMissingMessage);
        return;
      }

      await onCredentialRef.current({
        idToken,
        firstName: response.user?.name?.firstName?.trim() || undefined,
        lastName: response.user?.name?.lastName?.trim() || undefined,
      });
    } catch (error) {
      if (isAppleSignInCanceled(error)) {
        return;
      }

      onErrorRef.current(
        error instanceof Error && error.message.trim() ? error.message : authFailedMessage
      );
    }
  }, [authFailedMessage, credentialMissingMessage, isReady, isSubmitting]);

  if (!isAppleSignInConfigured || hasLoadError) {
    return null;
  }

  return (
    <SoftBox width="100%">
      <SoftButton
        variant="outlined"
        color="info"
        size="large"
        fullWidth
        onClick={handleClick}
        disabled={!isReady || isSubmitting}
        sx={{
          minHeight: APPLE_BUTTON_MIN_HEIGHT,
          position: "relative",
          borderWidth: "1px",
          borderColor: ({ palette }) => palette.grey[300],
          borderRadius: SOCIAL_BUTTON_RADIUS,
          backgroundColor: ({ palette }) => palette.common.white,
          color: ({ palette }) => palette.text.primary,
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          px: 2.25,
          "&:hover": {
            backgroundColor: ({ palette }) => palette.grey[100],
            borderColor: ({ palette }) => palette.grey[400],
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
          },
          "&.Mui-disabled": {
            backgroundColor: ({ palette }) => palette.grey[100],
            borderColor: ({ palette }) => palette.grey[300],
          },
        }}
      >
        <SoftBox display="flex" alignItems="center" justifyContent="center" width="100%" gap={1.5}>
          {isSubmitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <SoftBox
              component="img"
              src={appleLogo}
              alt=""
              aria-hidden="true"
              sx={{ width: 18, height: 18, flexShrink: 0 }}
            />
          )}
          <SoftTypography
            fontWeight="medium"
            color="dark"
            sx={{ fontSize: "0.95rem", lineHeight: 1.2, letterSpacing: "0.01em" }}
          >
            {label}
          </SoftTypography>
        </SoftBox>
      </SoftButton>
    </SoftBox>
  );
}
